import { gql, useMutation } from "@apollo/client";
import { Box, Checkbox, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import React, { useEffect, useState } from 'react';
import { UpercutFavoriteType } from './types';
import { useTalentOperations } from '../../context/TalentOperationsContext';

const OPTIONS: Array<{
    type: UpercutFavoriteType;
    label: string;
    description: string;
    icon: string;
}> = [
    {
        type: 'public',
        label: 'Favori public',
        description:
            'Les profils de cette liste sont visibles par toutes les personnes ayant un compte Upercut dans votre entreprise.',
        icon: 'fi-br-world',
    },
    {
        type: 'private',
        label: 'Favori privé',
        description:
            'Les profils de cette liste seront uniquement visibles par vous et votre direction.',
        icon: 'fi-br-lock',
    },
];

export interface FavoriteToggleProps {
    /** Talent's upercutFavorites array from GraphQL query or map of talent ID to favorites for multiple talents */
    upercutFavorites?: Array<{
        _id: string;
        type: string;
        upercutId?: string;
        createdAt: string;
    }> | Record<string, Array<{
        _id: string;
        type: string;
        upercutId?: string;
        createdAt: string;
    }>>;
    /** Count of talents in the public favorites list */
    publicCount?: number;
    /** Count of talents in the private favorites list */
    privateCount?: number;
    /** Called when the user confirms the selection from the dialog */
    loading?: boolean;
    /** Single talent ID or array of talent IDs */
    talentIds: string | string[];
    /** Visual size aligned with avatar size */
    size?: 'sm' | 'md' | 'lg' | 'xl';
}



export const FavoriteToggle: React.FC<FavoriteToggleProps> = ({
    upercutFavorites,
    publicCount = 0,
    privateCount = 0,
    loading = false,
    talentIds,
    size = 'sm',
}) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [initialSelectedTypes, setInitialSelectedTypes] = useState<UpercutFavoriteType[]>([]);
    const [selectedTypes, setSelectedTypes] = useState<UpercutFavoriteType[]>([]);

    const { updateFavorites, isUpdatingFavorites } = useTalentOperations();

    // Normalize talent IDs to array for consistent processing
    const talentIdsArray = Array.isArray(talentIds) ? talentIds : [talentIds];
    const isMultipleSelection = talentIdsArray.length > 1;

    // Calculate favorite states for multiple talents
    const calculateFavoriteStates = () => {
        if (!isMultipleSelection) {
            // Single talent - use existing logic
            const favoritesArray = Array.isArray(upercutFavorites) ? upercutFavorites : [];
            const hasPublic = favoritesArray.some(fav => fav.type === 'public');
            const hasPrivate = favoritesArray.some(fav => fav.type === 'private');
            return {
                public: { hasAll: hasPublic, hasNone: !hasPublic, hasPartial: false },
                private: { hasAll: hasPrivate, hasNone: !hasPrivate, hasPartial: false }
            };
        }

        // Multiple talents - check favorite states across all talents
        // Ensure we have a valid favorites map for multiple selection
        const favoritesMap = (!Array.isArray(upercutFavorites) && upercutFavorites) 
            ? upercutFavorites as Record<string, Array<{ _id: string; type: string; upercutId?: string; createdAt: string }>>
            : {};
        
        const publicCount = talentIdsArray.reduce((count, talentId) => {
            const talentFavorites = favoritesMap[talentId] || [];
            return count + (talentFavorites.some(fav => fav.type === 'public') ? 1 : 0);
        }, 0);
        
        const privateCount = talentIdsArray.reduce((count, talentId) => {
            const talentFavorites = favoritesMap[talentId] || [];
            return count + (talentFavorites.some(fav => fav.type === 'private') ? 1 : 0);
        }, 0);

        return {
            public: {
                hasAll: publicCount === talentIdsArray.length,
                hasNone: publicCount === 0,
                hasPartial: publicCount > 0 && publicCount < talentIdsArray.length
            },
            private: {
                hasAll: privateCount === talentIdsArray.length,
                hasNone: privateCount === 0,
                hasPartial: privateCount > 0 && privateCount < talentIdsArray.length
            }
        };
    };

    const favoriteStates = calculateFavoriteStates();

    // Calculate current state from favorite states
    const hasPublicFavorite = favoriteStates.public.hasAll;
    const hasPrivateFavorite = favoriteStates.private.hasAll;
    const isFavorite = hasPublicFavorite || hasPrivateFavorite || favoriteStates.public.hasPartial || favoriteStates.private.hasPartial;
    
    const currentSelectedTypes: UpercutFavoriteType[] = [
        ...(favoriteStates.public.hasAll ? ['public' as const] : []),
        ...(favoriteStates.private.hasAll ? ['private' as const] : [])
    ];

    // Update selected types when dialog opens
    useEffect(() => {
        if (dialogOpen) {
            const currentTypes: UpercutFavoriteType[] = [
                ...(favoriteStates.public.hasAll ? ['public' as const] : []),
                ...(favoriteStates.private.hasAll ? ['private' as const] : [])
            ];
            setSelectedTypes(currentTypes);
        }
    }, [dialogOpen, favoriteStates.public.hasAll, favoriteStates.private.hasAll]);

    // Determine heart color: red for public, orange for private only
    const getHeartColor = () => {
        if (favoriteStates.public.hasAll || favoriteStates.public.hasPartial) return '#FF4D4F'; // Red for public or partial public
        if (favoriteStates.private.hasAll || favoriteStates.private.hasPartial) return '#FF8C00'; // Orange for private only or partial private
        return '#A7A7A8'; // Gray for no favorites
    };

    const handleOpen = (event: React.MouseEvent) => {
        event.stopPropagation();
        setInitialSelectedTypes(currentSelectedTypes); // Store initial state when opening
        setDialogOpen(true);
    };

    const handleClose = () => setDialogOpen(false);

    const handleConfirm = async () => {
        // Prepare the current favorites data based on single vs multiple selection
        let currentFavoritesArray: Array<{ _id: string; type: string; upercutId?: string; createdAt: string; }> = [];
        
        if (!isMultipleSelection) {
            // Single talent - use favorites array directly
            currentFavoritesArray = Array.isArray(upercutFavorites) ? upercutFavorites : [];
        } else {
            // Multiple talents - aggregate all favorites from all selected talents
            const favoritesMap = (!Array.isArray(upercutFavorites) && upercutFavorites) 
                ? upercutFavorites as Record<string, Array<{ _id: string; type: string; upercutId?: string; createdAt: string; }>>
                : {};
                
            // Collect all unique favorites from all selected talents
            const allFavorites = new Map<string, { _id: string; type: string; upercutId?: string; createdAt: string; }>();
            
            talentIdsArray.forEach(talentId => {
                const talentFavorites = favoritesMap[talentId] || [];
                talentFavorites.forEach(fav => {
                    allFavorites.set(fav._id, fav);
                });
            });
            
            currentFavoritesArray = Array.from(allFavorites.values());
        }

        await updateFavorites(
            talentIdsArray, 
            currentFavoritesArray,
            initialSelectedTypes, 
            selectedTypes
        );
        
        setDialogOpen(false);
    };

    const toggleType = (type: UpercutFavoriteType) => {
        setSelectedTypes(prev =>
            prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type],
        );
    };

    const getCount = (type: UpercutFavoriteType) =>
        type === 'public' ? publicCount : privateCount;

    const getToggleStyleBySize = (toggleSize: FavoriteToggleProps['size']) => {
        switch (toggleSize) {
            case 'xl':
                return {
                    container: 36,
                    icon: 18,
                    top: '8px',
                    right: '6px',
                    iconPaddingTop: 3,
                    iconPaddingLeft: 2,
                };
            case 'lg':
                return {
                    container: 32,
                    icon: 16,
                    top: '7px',
                    right: '5px',
                    iconPaddingTop: 3,
                    iconPaddingLeft: 2,
                };
            case 'md':
                return {
                    container: 28,
                    icon: 14,
                    top: '5px',
                    right: '4px',
                    iconPaddingTop: 2,
                    iconPaddingLeft: 2,
                };
            case 'sm':
            default:
                return {
                    container: 24,
                    icon: 12,
                    top: '3px',
                    right: '2px',
                    iconPaddingTop: 2,
                    iconPaddingLeft: 2,
                };
        }
    };

    const toggleSizeStyle = getToggleStyleBySize(size);

    return (
        <>
            <Box
                sx={{
                    height: `${toggleSizeStyle.container}px`,
                    width: `${toggleSizeStyle.container}px`,
                    borderRadius: '50%',
                    position: 'absolute',
                    top: toggleSizeStyle.top,
                    right: toggleSizeStyle.right,
                    border: theme => `1px solid ${theme.palette.border?.main ?? 'rgba(167, 167, 168, 1)'}`,
                    background: theme => theme.palette.background.default,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s ease',
                    cursor: 'pointer',
                    '&:hover': {
                        background: theme =>
                            isFavorite
                                ? `${theme.palette.errorRed?.main ?? '#FF4D4F'}30`
                                : theme.palette.action.hover,
                    },
                }}
                onClick={handleOpen}>
                <i
                    className={isFavorite ? 'fi fi-sr-heart' : 'fi fi-br-heart'}
                    style={{
                        color: getHeartColor(),
                        fontSize: `${toggleSizeStyle.icon}px`,
                        paddingTop: toggleSizeStyle.iconPaddingTop,
                        paddingLeft: toggleSizeStyle.iconPaddingLeft,
                    }}
                />
            </Box>

            <Dialog
                open={dialogOpen}
                onClose={handleClose}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                    },
                }}>
                {/* ── Header ── */}
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}>
                    <Typography variant="h6" fontWeight={700} fontSize={20}>
                        Changer les favoris
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton size="small" onClick={handleClose} sx={{ ml: 0.5 }}>
                            <i className="fi fi-br-cross" style={{ fontSize: 12, paddingTop: 2 }} />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {/* ── Subtitle ── */}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.5 }}>
                        En décochant l'un des types de favoris, il sera supprimé de la liste concernée.
                    </Typography>

                    {/* ── Option cards ── */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {OPTIONS.map(option => {
                            const isChecked = selectedTypes.includes(option.type);
                            const count = getCount(option.type);
                            return (
                                <Box
                                    key={option.type}
                                    onClick={() => toggleType(option.type)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: '12px 14px',
                                        borderRadius: '12px',
                                        border: theme =>
                                            `1.5px solid ${isChecked ? theme.palette.primary.main : theme.palette.border?.main ?? '#E0E0E0'}`,
                                        backgroundColor: theme =>
                                            isChecked
                                                ? `${theme.palette.primary.main}0D`
                                                : theme.palette.background.paper,
                                        cursor: 'pointer',
                                        transition: 'border-color 0.2s, background-color 0.2s',
                                    }}>
                                    {/* Checkbox with indeterminate state support */}
                                    <Checkbox
                                        checked={isChecked}
                                        indeterminate={!isChecked && (
                                            option.type === 'public' ? favoriteStates.public.hasPartial :
                                            option.type === 'private' ? favoriteStates.private.hasPartial : false
                                        )}
                                        disabled={loading || isUpdatingFavorites}
                                        onClick={e => e.stopPropagation()}
                                        onChange={() => toggleType(option.type)}
                                        sx={{ p: 0, mr: 0.5 }}
                                    />

                                    {/* Icon circle with count badge */}
                                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                        <Box
                                            sx={{
                                                width: 52,
                                                height: 52,
                                                borderRadius: '50%',
                                                backgroundColor: theme => theme.palette.action.hover,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                            <i
                                                className={`fi ${option.icon}`}
                                                style={{ fontSize: 22, paddingTop: 4 }}
                                            />
                                        </Box>
                                        {/* Count badge */}
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                bottom: -4,
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                minWidth: 18,
                                                height: 18,
                                                borderRadius: '9px',
                                                backgroundColor: theme => theme.palette.background.paper,
                                                border: theme => `1px solid ${theme.palette.border?.main ?? '#E0E0E0'}`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                px: 0.5,
                                            }}>
                                            <Typography
                                                sx={{ fontSize: 10, fontWeight: 600, lineHeight: 1 }}>
                                                {count}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Text */}
                                    <Box sx={{ flex: 1 }}>
                                        <Typography fontWeight={600} fontSize={15} sx={{ mb: 0.25 }}>
                                            {option.label}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" fontSize={12} lineHeight={1.4}>
                                            {option.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* ── Valider button ── */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                        <Box
                            component="button"
                            disabled={loading || isUpdatingFavorites}
                            onClick={handleConfirm}
                            sx={{
                                px: 3,
                                py: 1.25,
                                borderRadius: '24px',
                                border: 'none',
                                backgroundColor: theme => theme.palette.primary.main,
                                color: theme => theme.palette.neutralWhite?.main ?? '#fff',
                                fontWeight: 600,
                                fontSize: 15,
                                cursor: (loading || isUpdatingFavorites) ? 'not-allowed' : 'pointer',
                                opacity: (loading || isUpdatingFavorites) ? 0.7 : 1,
                                transition: 'opacity 0.2s',
                                '&:hover': { opacity: (loading || isUpdatingFavorites) ? 0.7 : 0.88 },
                            }}>
                            Valider
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};
